<main>
  <loading-overlay [isLoading]="isLoading() > 0" />

  <header>
    <div class="col-sm">
      <div class="row-start-center gap-3">
        <h1>
          {{
            isNew()
              ? ('BankTransaction_Details_New' | translateWF: 'New Bank Transaction')
              : ('BankTransaction' | translateWF: 'Bank Transaction') + ' ' + (form.controls.number.value || '')
          }}
        </h1>
        @if (!isNew()) {
          <p-tag
            [severity]="status() === BankTransactionStatus.POSTED ? 'success' : 'info'"
            [value]="status() === BankTransactionStatus.POSTED ? ('Posted' | translateWF: 'Posted') : ('Draft' | translateWF: 'Draft')"
          />
        }
      </div>
      <small class="text-surface-500">{{
        'BankTransaction_Details_Hint'
          | translateWF
            : 'Posts immediately, no cash in
        transit.'
      }}</small>
    </div>
    <div class="buttons">
      <span class="separator"></span>
      <p-button [label]="'Cancel' | translateWF: 'Cancel'" variant="text" (click)="cancel()" />
      @if (isEditable()) {
        <p-button [label]="'BankTransaction_SaveDraft' | translateWF: 'Save as draft'" icon="pi pi-save" variant="outlined" (click)="saveDraft()" [disabled]="saving()" />
        <p-button [label]="'BankTransaction_Post' | translateWF: 'Post transaction'" icon="pi pi-check" severity="success" (click)="post()" [disabled]="saving() || isNew()" />
      }
    </div>
  </header>

  <div class="content">
    <form [formGroup]="form">
      <!-- Instrument, money, charges and the live entry preview all live in the shared movement form: a
           transfer leg is the same thing with different labels, and keeping two copies is how the two
           drifted apart in the first place (#5089). What stays here is what only this screen knows — which
           account it hits, its direction, and the entry it already posted. -->
      <app-bank-movement-form
        [mode]="movementMode()"
        [controls]="movementControls"
        [currencyLabel]="currencyLabel()"
        [editable]="isEditable()"
        [showPreview]="isEditable()"
        [previewLines]="previewLines()"
        [previewTitle]="showAccounting() ? ('BankTransaction_Posted' | translateWF: 'Posted') : ('BankTransaction_Pending' | translateWF: 'Pending')"
        [previewSubtitle]="(form.controls.transactionDate.value | date: 'dd/MM/yyyy') || ''"
        [instrumentTypeOptions]="instrumentTypeOptions()"
        [feeTypeOptions]="feeTypeOptions()"
        [counterAccountRequired]="requiresCounterAccount()"
        [counterAccountLabel]="
          isIncome()
            ? ('BankTransaction_CounterAccount_In' | translateWF: 'Offsetting account (what it came from)')
            : ('BankTransaction_CounterAccount_Out' | translateWF: 'Offsetting account (what it went to)')
        "
      >
        <div movementContext class="col">
          <!-- The account this movement hits. Its currency IS the movement's currency. -->
          <p-panel>
            <p-card>
              <div class="bank-header">
                @if (bankAccount(); as account) {
                  <div class="bank-header__icon grid place-items-center w-12 h-12 rounded-[10px] shrink-0"><i class="pi pi-building-columns"></i></div>
                  <div class="bank-header__body">
                    <div class="bank-header__title">
                      <strong>{{ account.name }}</strong>
                      <span class="text-surface-500"> · {{ account.bankName }}</span>
                    </div>
                    <div class="text-surface-500 text-sm">
                      {{ account.code }} · {{ 'BankAccount_Number' | translateWF: 'Acct' }} {{ account.bankAccountNumber }} · {{ currencyLabel() }}
                    </div>
                  </div>
                } @else {
                  <div class="form-box w-full">
                    <label for="bankAccountCode">{{ fieldLabels.bankAccountCode | translateWF: 'Bank account' }} <span class="text-red-500">*</span></label>
                    <p-select
                      id="bankAccountCode"
                      [options]="bankAccountOptions()"
                      formControlName="bankAccountCode"
                      optionLabel="code"
                      optionValue="code"
                      [filter]="true"
                      filterBy="code,name"
                      appendTo="body"
                      styleClass="w-full"
                      [placeholder]="'BankTransaction_SelectAccount' | translateWF: 'Select the account this movement hits'"
                      (onChange)="onBankAccountChange($event.value)"
                      [ngClass]="{ 'ng-invalid ng-dirty': form.controls.bankAccountCode.invalid && form.controls.bankAccountCode.touched }"
                    />
                  </div>
                }
              </div>
            </p-card>
          </p-panel>

          <!-- Direction is fixed at creation: flipping it would silently swap which side of every account the
             movement lands on, so the only way out is to start over. -->
          <div class="row-start-center gap-3">
            <p-tag [severity]="directionSeverity()" [value]="directionLabel()" [icon]="isIncome() ? 'pi pi-arrow-down-left' : 'pi pi-arrow-up-right'" />
            @if (isNew()) {
              <small class="text-surface-500">
                {{ 'BankTransaction_WrongDirection' | translateWF: 'Wrong direction?' }}
                <a class="text-primary cursor-pointer" (click)="cancel()">{{ 'Cancel' | translateWF: 'Cancel' }}</a>
                {{ 'BankTransaction_AndStartOver' | translateWF: 'and start over.' }}
              </small>
            }
          </div>
        </div>

        <!-- The entry as it ACTUALLY posted, read from the ledger rather than rebuilt from this form —
             which is why it replaces the preview above rather than sitting beside it. Brings the entry
             number, the balanced total and the `View full entry` button (#5026) with it, so a posted
             movement is one click from its posting instead of naming a document number you had to go
             find. The same component the transfer, funding, advance, withholding, AP document and AP
             payment screens use. -->
        @if (showAccounting()) {
          <app-journal-entry-summary movementAside [references]="journalEntryReference()" [currencyLabel]="currencyLabel()" />
        }

        <div movementAside>
          <p-panel>
            <p-card>
              <div class="form-box">
                <label>{{ 'BankTransaction_Notes' | translateWF: 'Notes' }}</label>
                <textarea pTextarea formControlName="notes" rows="3" class="w-full"></textarea>
              </div>
            </p-card>
          </p-panel>
        </div>
      </app-bank-movement-form>
    </form>
  </div>
</main>
